import React, { useCallback, useEffect, useRef, useState } from "react";
import { DeviceSelector, PermissionRequest } from "../camera/CameraControls.tsx";
import PurchaseControls from "./PurchaseControls.tsx";
import ManualPurchaseControls from "./ManualPurchaseControls.tsx";
import BarcodeNotFound from "./BarcodeNotFound.tsx";
import { usePurchaseScanning } from "hooks/usePurchaseScanning.ts";
import { useCameraContext } from "../camera/CameraContext.tsx";
import ExpiryCaptureOverlay from "./ExpiryCaptureOverlay.tsx";
import { ExpiryExtractionResult, extractExpiryDateFromImage } from "utils/aiExpiryDate.ts";
import { captureCenteredExpiryCrop } from "./expiryCapture.ts";
import "./PurchaseFeedContent.css";

type ExpiryCaptureState = 'idle' | 'framing' | 'extracting';

const PurchaseFeedContent: React.FC = () => {
  const {
    scannedBarcode,
    purchaseMode,
    scannedProduct,
    unknownProduct,
    barcodeNotFound,
    clearScan,
  } = usePurchaseScanning();
  const { videoRef, pauseProcessing, resumeProcessing, scanning } = useCameraContext();
  const [manualOpen, setManualOpen] = useState(false);
  const [expiryCaptureState, setExpiryCaptureState] = useState<ExpiryCaptureState>('idle');
  const [capturedPreviewUrl, setCapturedPreviewUrl] = useState<string | null>(null);
  const expiryCaptureResolverRef = useRef<{
    resolve: (value: ExpiryExtractionResult | null) => void;
    reject: (reason?: unknown) => void;
  } | null>(null);
  const resumeScanningRef = useRef(false);

  // Auto-close manual panel if a barcode purchase begins
  useEffect(() => {
    if (purchaseMode === 'purchasing') {
      setManualOpen(false);
    }
  }, [purchaseMode]);

  const handleManualEntry = () => {
    clearScan(); // Clear the current scan state
    setManualOpen(true); // Open the manual entry panel
  };

  const clearCapturedPreview = useCallback(() => {
    setCapturedPreviewUrl((current) => {
      if (current) {
        URL.revokeObjectURL(current);
      }
      return null;
    });
  }, []);

  const finishExpiryCapture = useCallback((result: ExpiryExtractionResult | null) => {
    expiryCaptureResolverRef.current?.resolve(result);
    expiryCaptureResolverRef.current = null;
    setExpiryCaptureState('idle');
    clearCapturedPreview();
    if (resumeScanningRef.current) {
      resumeProcessing();
      resumeScanningRef.current = false;
    }
  }, [clearCapturedPreview, resumeProcessing]);

  const failExpiryCapture = useCallback((error: unknown) => {
    expiryCaptureResolverRef.current?.reject(error);
    expiryCaptureResolverRef.current = null;
    setExpiryCaptureState('idle');
    clearCapturedPreview();
    if (resumeScanningRef.current) {
      resumeProcessing();
      resumeScanningRef.current = false;
    }
  }, [clearCapturedPreview, resumeProcessing]);

  const requestExpiryPhotoCapture = useCallback(() => {
    if (expiryCaptureResolverRef.current) {
      return Promise.reject(new Error('Expiry capture is already active'));
    }

    if (!videoRef.current) {
      return Promise.reject(new Error('Camera preview is not ready yet'));
    }

    resumeScanningRef.current = scanning;
    pauseProcessing();
    clearCapturedPreview();
    setExpiryCaptureState('framing');

    return new Promise<ExpiryExtractionResult | null>((resolve, reject) => {
      expiryCaptureResolverRef.current = { resolve, reject };
    });
  }, [clearCapturedPreview, pauseProcessing, scanning, videoRef]);

  const handleCancelExpiryCapture = useCallback(() => {
    finishExpiryCapture(null);
  }, [finishExpiryCapture]);

  const handleCaptureExpiry = useCallback(async () => {
    if (!videoRef.current) {
      failExpiryCapture(new Error('Camera preview is not ready yet'));
      return;
    }

    setExpiryCaptureState('extracting');

    try {
      const image = await captureCenteredExpiryCrop(videoRef.current);
      setCapturedPreviewUrl((current) => {
        if (current) {
          URL.revokeObjectURL(current);
        }
        return URL.createObjectURL(image);
      });
      const result = await extractExpiryDateFromImage(image);
      finishExpiryCapture(result);
    } catch (error) {
      failExpiryCapture(error);
    }
  }, [failExpiryCapture, finishExpiryCapture, videoRef]);

  useEffect(() => {
    return () => {
      if (capturedPreviewUrl) {
        URL.revokeObjectURL(capturedPreviewUrl);
      }
    };
  }, [capturedPreviewUrl]);

  return (
    <div className={`purchase-feed ${expiryCaptureState !== 'idle' ? 'purchase-feed--expiry-capture' : ''}`}>
      {expiryCaptureState !== 'idle' && (
        <ExpiryCaptureOverlay
          isExtracting={expiryCaptureState === 'extracting'}
          previewUrl={capturedPreviewUrl}
          onCapture={handleCaptureExpiry}
          onCancel={handleCancelExpiryCapture}
        />
      )}

      {purchaseMode === 'purchasing' && (
        <>
          {scannedProduct ? (
            <PurchaseControls
              existingProduct={scannedProduct}
              unknownProduct={undefined}
              onClear={clearScan}
              onRequestPhotoCapture={requestExpiryPhotoCapture}
            />
          ) : unknownProduct ? (
            <PurchaseControls
              existingProduct={undefined}
              unknownProduct={unknownProduct}
              onClear={clearScan}
              onRequestPhotoCapture={requestExpiryPhotoCapture}
            />
          ) : barcodeNotFound ? (
            <BarcodeNotFound
              barcode={barcodeNotFound}
              onClear={clearScan}
              onManualEntry={handleManualEntry}
              className="purchase-feed__barcode-not-found"
            />
          ) : null}
        </>
      )}

      {!scannedBarcode && purchaseMode !== 'purchasing' && manualOpen && (
        <ManualPurchaseControls
          onClose={() => setManualOpen(false)}
          onRequestPhotoCapture={requestExpiryPhotoCapture}
        />
      )}

      {scannedBarcode && (
        <div className="purchase-feed__barcode">
          Scanned: {scannedBarcode}
        </div>
      )}

      <div className={`purchase-feed__bottom-controls ${expiryCaptureState !== 'idle' ? 'purchase-feed__bottom-controls--hidden' : ''}`}>
        <PermissionRequest className="camera-overlay" />
        <DeviceSelector className="camera-overlay" fullscreen />
        {!manualOpen && purchaseMode !== 'purchasing' && (
          <>
            <span className="purchase-feed__scan-hint">Scan a barcode or tap below to pick manually</span>
            <button
              type="button"
              className="purchase-feed__manual-button camera-overlay"
              onClick={() => setManualOpen(true)}
            >
              Manual Entry
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default PurchaseFeedContent; 
